import {
  BARCODE_ELEMENT,
  COMPLEX_DOCUMENT_PAGE_WRAPPER,
  ELEMENT_TYPES,
  FILLABLE_ELEMENT,
  IMAGE_ELEMENT,
  STATIC_TABLE_ELEMENT,
  TEXT_ELEMENT,
} from './complexDocumentConstants';
import { VARIABLE_OBJECT_TYPES } from './constants/appConstants';
import axiosCall from './services/interceptor';
import { uploadImage } from './services/uploadAPI';
import { VARIABLE_TYPE } from './constants/appConstants';
import { addVariableCategory, getAllVariableCategory } from './services/variablesAPI';
import { getIndexedDBData, putIndexedDBData } from './indexedDBUtil';

export const EXPORT_FILE_NAME = 'pds_export.json';

export const extractVariables = (variableContract = [], gVariables) => {
  const variables = [];
  variableContract.forEach(({ variableId, type, content }) => {
    const variable = gVariables.find(gVar => gVar.id === variableId);
    if (variable) {
      variables.push(variable);
    } else if (type === VARIABLE_TYPE.CHART_OBJECT) {
      content.forEach(({ variableId }) => {
        const chartVar = gVariables.find(gVar => gVar.id === variableId);
        if (chartVar) {
          variables.push(chartVar);
        }
      });
    } else if (type === VARIABLE_TYPE.OBJECT) {
      content.forEach(objectContent => {
        const objVar = gVariables.find(gVar => gVar.id === objectContent.variableId);
        if (objVar) {
          variables.push(objVar);
        } else if (objectContent.type === VARIABLE_TYPE.CHART_OBJECT) {
          objectContent.content.forEach(({ variableId }) => {
            const chartVar = gVariables.find(gVar => gVar.id === variableId);
            if (chartVar) {
              variables.push(chartVar);
            }
          });
        }
      });
    }
  });
  return variables;
};

export const getNewVariableId = (variableId, variableIdMap) => {
  if (!variableId) {
    return variableId;
  }
  variableId = Number(variableId);
  if (variableIdMap.has(variableId)) {
    return variableIdMap.get(variableId).id;
  }
  return variableId;
};

export const modifyTextBlockContents = (contents, variableIdMap) => {
  return contents.map(textContent => {
    if (textContent?.style?.isVariable) {
      const textVariable = textContent.content.replaceAll('{', '').replaceAll('}', '');
      textContent.content = `{${getNewVariableId(textVariable, variableIdMap)}}`;
    }
    if (textContent?.content === "custom_logic" && Array.isArray(textContent.contents)) {
      textContent.contents = modifyTextBlockContents(textContent.contents, variableIdMap);
    }
    return textContent;
  });
};

export const modifyDynamicTableBlockContents = (contents, variableIdMap) => {
  return contents.map(tableContent => {
    if (tableContent.headerContent) {
      tableContent.headerContent.contents = modifyTextBlockContents(
        tableContent.headerContent.contents,
        variableIdMap,
      );
    }
    if (tableContent.groupHeaderContent) {
      tableContent.groupHeaderContent.contents = modifyTextBlockContents(
        tableContent.groupHeaderContent.contents,
        variableIdMap,
      );
    }
    if (tableContent.content) {
      tableContent.content.contents = modifyTextBlockContents(
        tableContent.content.contents,
        variableIdMap,
      );
    }
    return tableContent;
  });
};

export const modifyBlockContents = async (contents, variableIdMap, uploadedUrlsMap, uploadImages = true) => {
  let openImages = await getIndexedDBData('openImages');
  openImages = openImages || [];
  for (let i = 0; i < contents.length; i++) {
    const content = contents[i];
    const { contentType, url } = content;
    if (contentType === TEXT_ELEMENT) {
      content.contents = modifyTextBlockContents(content.contents, variableIdMap);
    } else if (contentType === IMAGE_ELEMENT) {
      if (content.style.isVariable) {
        const oldVariableId = content.content.replaceAll('{', '').replaceAll('}', '');
        content.content = `{${getNewVariableId(oldVariableId, variableIdMap)}}`;
      }
      if (uploadedUrlsMap.has(url)) {
        content.url = uploadedUrlsMap.get(url);
      } else if (uploadImages) {
        let imageFile = undefined;
        let fileName = undefined;
        if (url.startsWith('data')) {
          fileName = openImages.find(e => e.imageLink.startsWith(url))?.path.split('/')[1];
          const imageResponse = await fetch(url);
          const blob = await imageResponse.blob();
          imageFile = new File([blob], fileName);
        } else {
          const splitUrlArray = url.split('/');
          fileName = splitUrlArray[splitUrlArray.length - 1];
          const imageResponse = await axiosCall('get', url, { responseType: 'blob' }, false);
          imageFile = imageResponse?.data;
        }
        if (imageFile) {
          const response = await uploadImage(imageFile, fileName);
          if (response?.imageLink) {
            content.url = response.imageLink;
            uploadedUrlsMap.set(url, response.imageLink);
          }
        }
      }
    } else if (contentType === BARCODE_ELEMENT) {
      if (content.style.isVariable) {
        const oldVariableId = content.content;
        content.content = `${getNewVariableId(oldVariableId, variableIdMap)}`;
      }
    } else if (contentType === FILLABLE_ELEMENT) {
      console.log({ content });
    } else if (contentType === STATIC_TABLE_ELEMENT) {
      content.contents = content.contents.map(tableContent => {
      tableContent.contents = modifyTextBlockContents(tableContent.contents, variableIdMap);
      return tableContent;
    });
    } else if (contentType === ELEMENT_TYPES.CHART) {
      const updateChartVariables = chartContents => {
        const {
          labelVariable,
          valueVariable,
          descVariable,
          seriesNameVariable,
          seriesValueVariable,
        } = chartContents;
        chartContents.labelVariable = getNewVariableId(labelVariable, variableIdMap);
        if (chartContents.valueVariable) {
          chartContents.valueVariable = getNewVariableId(valueVariable, variableIdMap);
        }
        if (chartContents.descVariable) {
          chartContents.descVariable = getNewVariableId(descVariable, variableIdMap);
        }
        if (chartContents.seriesValueVariable) {
          chartContents.seriesValueVariable = getNewVariableId(seriesValueVariable, variableIdMap);
        }
        if (chartContents.seriesNameVariable) {
          chartContents.seriesNameVariable = getNewVariableId(seriesNameVariable, variableIdMap);
        }
        return chartContents;
      };
      content.contents = updateChartVariables(content.contents);
      if (content.contents.dataRequest) {
        content.contents.dataRequest = updateChartVariables(content.contents.dataRequest);
      }
    }
  }
  return contents;
};

export const modifyStaticTableBlockContents = async (contents, variableIdMap, uploadedUrlsMap, uploadImages) => {
  for (let i = 0; i < contents.length; i++) {
    const content = contents[i];
    content.contents = await modifyBlockContents(content.contents, variableIdMap, uploadedUrlsMap, uploadImages);
  }
  return contents;
};

export const modifyVariableContract = (variableContract, variableIdMap) => {
  return variableContract.map(blockVariable => {
    if (VARIABLE_OBJECT_TYPES.includes(blockVariable.type)) {
      blockVariable.content = modifyVariableContract(blockVariable.content, variableIdMap);
    } else if (blockVariable.variableId) {
      const newVariable = variableIdMap.get(blockVariable.variableId);
      blockVariable.variableId = newVariable.id;
      blockVariable.type = newVariable.type === 'ListString' ? 'listOfStrings' : newVariable.type.toLowerCase();
    }
    return blockVariable;
  });
};

export const importVariables = async (variablesToExport, globalVariables, store, allVariablesCategory = undefined, makeAction = true) => {
  const variablesToImport = variablesToExport;
  const categoryCache = {};
  const variableIdMap = new Map();

  const setVariableMapValue = (key, value) => {
    if (!variableIdMap.has(key)) {
      variableIdMap.set(key, value);
    }
  };

  if (allVariablesCategory === undefined) {
    const { data } = await getAllVariableCategory();
    allVariablesCategory = data;
  }

  const getUpdatedName = (variableCatName, name, type) => {
    const placeHolderText = `${variableCatName}.${name}`;
    const existingVariable = globalVariables.find(
      gVar => gVar.placeHolderText.toLowerCase() === placeHolderText.toLowerCase(),
    );
    const typeMismatch = existingVariable && existingVariable.type !== type;
    if (typeMismatch) {
      name = name + '_01';
      return getUpdatedName(variableCatName, name, type);
    }
    return [existingVariable, name, typeMismatch];
  };

  for (let i = 0; i < variablesToImport.length; i++) {
    const variable = variablesToImport[i];
    const {
      variableCategoryName: variableCatName,
      exampleValue,
      name,
      length,
      type,
      id,
      count,
      mandatory = false,
      checkLength = false
    } = variable;
    let variableCategory = allVariablesCategory.find(
      ({ name }) => name.toLowerCase() === variableCatName.toLowerCase(),
    );
    let catId = variableCategory?.id;
    if (!catId && !categoryCache[variableCatName]) {
      const catResponse = await addVariableCategory({ name: variableCatName });
      if (catResponse?.data?.id) {
        catId = catResponse.data.id;
      }
    }
    if (catId || categoryCache[variableCatName]) {
      if (!categoryCache[variableCatName]) {
        categoryCache[variableCatName] = catId;
      }
      const [existingVariable, uName, typeMismatch] = getUpdatedName(variableCatName, name, type);
      if (makeAction && (!existingVariable || typeMismatch)) {
        const newVariable = {
          name: uName,
          placeHolderText: `${variableCatName}.${uName}`,
          variableCategoryId: catId || categoryCache[variableCatName],
          variableCategoryName: variableCatName,
          length: length,
          exampleValue: exampleValue,
          type: type,
          count: count,
          mandatory,
          checkLength
        };
        const addVariableData = await store.addNewVariable(newVariable);
        if (addVariableData && Array.isArray(addVariableData)) {
          const [newVar] = addVariableData;
          setVariableMapValue(id, newVar);
        }
      } else {
        setVariableMapValue(id, existingVariable);
      }
    }
  }
  return variableIdMap;
};

// returns all unique fonts in use on the current page
export const listAllUsedFonts = () => {
  let fonts = [];
  const pageElement = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER) || document.body;
  for (let node of pageElement.querySelectorAll('*')) {
    if (!node.style) continue;
    for (let pseudo of ['', ':before', ':after']) {
      let fontFamily = getComputedStyle(node, pseudo).fontFamily;
      fonts = fonts.concat(fontFamily.split(/\n*,\n*/g));
    }
  }
  // Remove duplicate elements from fonts array
  // and remove the surrounding quotes around elements
  return [...new Set(fonts)].map(font => font.replace(/^\s*['"]([^'"]*)['"]\s*$/, '$1').trim());
};

export const partialMatchKey = (object, testString) => {
  let matchedKey;
  Object.keys(object).find(key => {
    if (key.endsWith(testString)) {
      matchedKey = key;
      return true;
    }
    return false;
  });
  return matchedKey;
};

export const allMatchKey = (object, testString, include = true) => {
  let matchedKeys = Object.keys(object).filter(key => {
    if (key.startsWith('.') || key.startsWith('_')) return false;
    if (key.endsWith(testString)) {
      return include;
    }
    return !include;
  });
  return matchedKeys;
};
